// @flow
import * as React from 'react'
import * as topojson from 'topojson-client'
import './styles.scss'
import config from 'config'
import ChoroplethMap from 'components/choroplethMap/'
import { SVGContainer } from 'components/svgContainer'
import ActiveMunicipalityPath from 'components/activeMunicipality'
import { geoIdentity, geoPath } from 'd3-geo'
import { nest } from 'd3-collection'
import hash from 'object-hash'
import EventListenerMap from 'components/eventListenerMap'

// region Constants
const seen = config.seen
// endregion

// region Type Definitions
export type TaxMapProps = {
  containerHeightRatio: number,
  filter?: {
    property: string,
    id: number
  },
  mouseOverHandler: (number, MouseEvent) => void,
  patternScale: number
}

export type GeojsonFeature = {
  type: string,
  properties: { [id: string]: number | string },
  geometry: { type: string, coordinates: Array<[number, number]> }
}

// endregion

/**
 * Draws a choropleth map of Swiss municipalities given a
 * data source.
 *
 * @param {object} props
 * @param {function} props.mouseOverHandler - How to deal with mouse over
 * events. This
 * is usually set up by the container module (using Redux)
 * @param {number} [props.containerHeightRatio=0.6] - What height to width
 * ratio the map container should have. This should be close to the
 * containing box of the region displayed. For Switzerland, which has a
 * landscape format, this is roughly 0.6.
 * @param {number} [props.patternScale=1] - When "zooming" into the map
 * using the filter, this is a way to manually tweak the scale of the cross
 * hatch pattern, in case it becomes too fine or coarse.
 * @param {object} [props.filter] - A filter for only showing speficic
 * municipalities
 * @param {string} props.filter.property - The name of the property to
 * filter for, using the properties of the Shapefiles. Examples include:
 * GMDNR for municipality IDs, KTNR for canton IDs, GRNR for «Grossregionen»
 * or BZNR for «Bezirke».
 * @param {number} props.filter.id - The ID to filter for.
 */
export function TaxMap (props: TaxMapProps): React.Node {
  const {
    mouseOverHandler,
    filter,
    containerHeightRatio = 0.6,
    patternScale = 1
  } = props
  const mapId = `map-${hash(props, {
    excludeKeys: key => {
      return key === 'mouseOverHandler'
    }
  }).substr(0, 5)}`

  let geometryCollection = config.gemeindegrenzen

  // If a filter is defined, we nest the list using the property and then
  // only pick the features we're interested in.
  if (filter && filter.property !== '') {
    let property = filter.property
    let id = filter.id
    let nestedFeatures = nest()
      .key((d: GeojsonFeature) => d.properties[property])
      .map(config.gemeindegrenzen.geometries)

    // replace the original list with the filtered list
    let filteredList = nestedFeatures.get(id)

    geometryCollection = {
      type: 'GeometryCollection',
      geometries: filteredList
    }
  }

  /**
   * Path generator function – needs to be defined in this part, since the
   * fitSize function depends on the applied filter for easy «zooming»
   */
  let pathGenerator: GeojsonFeature => string = geoPath().projection(
    geoIdentity().fitSize(
      [1000, 1000 * containerHeightRatio],
      topojson.feature(config.schweiz, geometryCollection)
    )
  )

  let seenPfade: Element<any> = topojson
    .feature(config.schweiz, seen)
    .features.map((see, index) => {
      return (
        <path
          key={index}
          d={pathGenerator(see)}
          fill='#b8d5e6'
          className='lake'
          stroke='#a1b2bd'
          strokeWidth={1}
          vectorEffect='non-scaling-stroke'
        />
      )
    })

  // TODO If you don't want to show Zurich districts, you might want to skip
  // this step ...
  const filteredListWithZurich = {
    type: 'GeometryCollection',
    geometries: geometryCollection.geometries.concat(
      config.zuercherKreise.geometries
    )
  }

  // TODO ... and directly add in the geometryCollection here
  const mesh = (
    <path
      d={pathGenerator(
        topojson.mesh(config.schweiz, filteredListWithZurich, (a, b) => {
          if (a.properties['GMDNR'] === 261 || b.properties['GMDNR'] === 261) {
            return false
          }
          return true
        })
      )}
      stroke='#111'
      strokeLinejoin='round'
      fill='transparent'
      strokeOpacity={0.33}
      strokeWidth={1}
      vectorEffect='non-scaling-stroke'
    />
  )

  // Feature Detection for Internet Explorer and Edge, as they don't support
  // Drop Shadow Filters.
  const supportsDropShadowFilter =
    typeof window.SVGFEDropShadowElement !== 'undefined'

  return (
    <div
      className='swiss map'
      id={mapId}
      style={{ paddingBottom: `${100 * containerHeightRatio}%` }}
      onMouseOver={event => mouseOverHandler(-1, event)}
    >
      <SVGContainer
        className='gemeinden non-interactive'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <defs>
          {supportsDropShadowFilter && (
            <filter id='shadow'>
              <feDropShadow dx='0 ' dy='0' stdDeviation='1' />
            </filter>
          )}
          <pattern
            id={`${mapId}-diagonalHatch`}
            width={2 * patternScale}
            height={2 * patternScale}
            patternTransform='rotate(45 0 0)'
            patternUnits='userSpaceOnUse'
          >
            <line
              x1='0'
              y1='0'
              x2='0'
              y2={4}
              vectorEffect='non-scaling-stroke'
              stroke='white'
              strokeOpacity='0.7'
              strokeWidth={1}
            />
          </pattern>
        </defs>
        <ChoroplethMap
          pathGenerator={pathGenerator}
          geometryCollection={geometryCollection}
          mapId={mapId}
        />
      </SVGContainer>

      <SVGContainer
        className='kantone non-interactive'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        {mesh}
      </SVGContainer>

      <SVGContainer
        className='seen non-interactive'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        {seenPfade}
      </SVGContainer>

      <SVGContainer
        className='active non-interactive'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <ActiveMunicipalityPath
          pathGenerator={pathGenerator}
          geometryCollection={geometryCollection}
          mapId={mapId}
        />
      </SVGContainer>

      <SVGContainer
        className='event-listener'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <EventListenerMap
          pathGenerator={pathGenerator}
          geometryCollection={geometryCollection}
          mapId={mapId}
        />
      </SVGContainer>
    </div>
  )
}

export default TaxMap
